import { Decimal } from 'decimal.js';
import { OrderPaymentType } from '../constants/order-payment-type.js';
import { businessDateOfTimestamp } from '../dates/business-day.js';
import { businessDateOfOrderPayment } from './business-date-of-order-payment.js';
// Same-day order: a delivery, then the reversal of an earlier-dated payment, then payments, then
// clears, then a reversal dated on its payment's own day (or before it), then a completion.
const RANK = {
    DELIVERY: 0,
    EARLIER_PAYMENT_REVERSAL: 1,
    PAYMENT: 2,
    BALANCE_CLEAR: 3,
    REVERSAL: 4,
    COMPLETION: 5,
};
const ZERO = new Decimal(0);
function paymentEvents(order, payment) {
    // reversed with no reversed_at never counts as applied at any T, so it leaves no trace.
    if (payment.reversed && !payment.reversedAt)
        return [];
    const amount = new Decimal(payment.amount);
    const isClear = payment.typeId === OrderPaymentType.BALANCE_CLEAR;
    const isCash = payment.typeId === OrderPaymentType.PAYMENT;
    const sortDate = businessDateOfOrderPayment(payment);
    const events = [
        {
            sortDate,
            eventDate: sortDate,
            rank: isClear ? RANK.BALANCE_CLEAR : RANK.PAYMENT,
            effectiveAt: payment.paymentDate,
            tieBreakId: payment.id,
            kind: isClear ? 'BALANCE_CLEAR' : 'PAYMENT',
            order,
            payment,
            apply: (state) => {
                state.applied = state.applied.plus(amount);
                if (isCash)
                    state.cash = state.cash.plus(amount);
            },
            receiptDelta: new Decimal(payment.receiptRemainder).negated(),
        },
    ];
    if (payment.reversed && payment.reversedAt) {
        const reversalDate = businessDateOfTimestamp(payment.reversedAt);
        events.push({
            // A reversal dated before its own payment never counted; sort it just after the payment so the pair cancels.
            sortDate: reversalDate > sortDate ? reversalDate : sortDate,
            eventDate: reversalDate,
            // Undoing an earlier day's payment comes before that day's new cash, so the replacement is not shown as an overpayment.
            rank: reversalDate > sortDate ? RANK.EARLIER_PAYMENT_REVERSAL : RANK.REVERSAL,
            effectiveAt: payment.reversedAt,
            tieBreakId: payment.id,
            kind: isClear ? 'CLEAR_REVERSED' : 'PAYMENT_REVERSED',
            order,
            payment,
            apply: (state) => {
                state.applied = state.applied.minus(amount);
                if (isCash)
                    state.cash = state.cash.minus(amount);
            },
            receiptDelta: new Decimal(payment.receiptRemainderReversed),
        });
    }
    return events;
}
function orderEvents(order) {
    const events = [];
    if (order.dateFirstShip) {
        events.push({
            sortDate: businessDateOfTimestamp(order.dateFirstShip),
            eventDate: businessDateOfTimestamp(order.dateFirstShip),
            rank: RANK.DELIVERY,
            effectiveAt: order.dateFirstShip,
            tieBreakId: order.salesOrderId,
            kind: order.isCreditReturn ? 'CREDIT_RETURN' : 'ORDER_DELIVERED',
            order,
            payment: null,
            apply: (state) => {
                state.delivered = true;
            },
            receiptDelta: ZERO,
        });
    }
    if (order.dateCompleted) {
        events.push({
            sortDate: businessDateOfTimestamp(order.dateCompleted),
            eventDate: businessDateOfTimestamp(order.dateCompleted),
            rank: RANK.COMPLETION,
            effectiveAt: order.dateCompleted,
            tieBreakId: order.salesOrderId,
            kind: 'ORDER_CLOSED',
            order,
            payment: null,
            apply: (state) => {
                state.completed = true;
            },
            receiptDelta: ZERO,
        });
    }
    return events;
}
export function compareLedgerEvents(left, right) {
    if (left.sortDate !== right.sortDate)
        return left.sortDate < right.sortDate ? -1 : 1;
    if (left.rank !== right.rank)
        return left.rank - right.rank;
    const byTime = left.effectiveAt.getTime() - right.effectiveAt.getTime();
    if (byTime !== 0)
        return byTime;
    if (left.tieBreakId === right.tieBreakId)
        return 0;
    return left.tieBreakId < right.tieBreakId ? -1 : 1;
}
/** Every ledger event of the given orders and their payments, unsorted. */
export function ledgerEventsFor(orders, payments) {
    const ordersById = new Map(orders.map((order) => [order.salesOrderId, order]));
    return [
        ...orders.flatMap(orderEvents),
        ...payments.flatMap((payment) => {
            const order = ordersById.get(payment.salesOrderId);
            return order ? paymentEvents(order, payment) : [];
        }),
    ];
}
//# sourceMappingURL=ar-ledger-events.js.map